import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { QuestionType, TicketingFormCategory } from "@simplyk/common";

import { Box, ButtonBase, Divider, Stack, SxProps, Theme } from "@mui/material";

import { getStepQuestionsV4Wording } from "../helpers/getStepQuestionsV4Wording";
import { getQuestionTypeSubtitle } from "../helpers/questionTypeIcons";
import { matchesFuzzyQuery } from "../helpers/matchesFuzzyQuery";
import { ScopeTogglePills } from "../ScopeTogglePills/ScopeTogglePills";
import {
  ADDRESS_OPTION_ID,
  AddQuestionSelection,
  BuyerPresetOption,
  CreateQuestionEntryPoint,
  CustomPropertyOption,
  GUEST_ADDRESS_OPTION_ID,
  GUEST_PHONE_NUMBER_OPTION_ID,
  GuestPresetOption,
  NOTIFICATION_PHONE_OPTION_ID,
  PHONE_NUMBER_OPTION_ID,
  QuestionScope,
} from "../types";

import { ExistingQuestionsList, SelectableItem } from "./ExistingQuestionsList";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DashboardAdaptiveModal } from "@/dashboard-design-system/AdaptiveModal";
import { DashboardIconBlock } from "@/dashboard-design-system/DashboardIconBlock/DashboardIconBlock";
import { DashboardSearchField } from "@/dashboard-design-system/SearchField";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { Add, Edit } from "@/icons/outlined";

const BUYER_PROPERTY_PREFIX = "buyer-prop:";
const GUEST_PROPERTY_PREFIX = "guest-prop:";

const EMPTY_SELECTED_IDS: string[] = [];
const EMPTY_DRAFTS: DraftQuestionOption[] = [];
const EMPTY_BUYER_PRESETS: BuyerPresetOption[] = [];

export interface DraftQuestionOption {
  id: string;
  scope: QuestionScope;
  label: string;
  subtitle?: ReactNode;
}

const scrollableListSx: SxProps<Theme> = {
  maxHeight: 360,
  overflowY: "auto",
  pb: 2,
  scrollbarWidth: "none",
  "&::-webkit-scrollbar": { display: "none" },
};

const createActionSx: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  gap: 1.5,
  padding: theme.spacing(1, 2),
  borderRadius: 1,
  transition: theme.transitions.create("background-color", {
    duration: theme.transitions.duration.shorter,
    easing: theme.transitions.easing.easeIn,
  }),
  "&:hover": { backgroundColor: theme.palette.surface.neutral.supershy },
});

interface AddQuestionModalProps {
  isOpen: boolean;
  initialScope: QuestionScope;
  formCategory?: TicketingFormCategory;
  onClose: () => void;
  existingCustomProperties: CustomPropertyOption[];
  isLoadingProperties: boolean;
  onAddSelected: (selection: AddQuestionSelection) => void;
  onCreateFromScratch: (
    scope: QuestionScope,
    selectedIds: string[],
    prefillBody?: string,
    entryPoint?: CreateQuestionEntryPoint
  ) => void;
  onSearch?: (audience: QuestionScope, hasResults: boolean) => void;
  showFullAddressOption: boolean;
  showGuestFullAddressOption?: boolean;
  showPhoneNumberOption: boolean;
  showNotificationPhoneOption?: boolean;
  notificationPhoneLabel?: string;
  showGuestPhoneNumberOption?: boolean;
  /** Buyer rows backed by form configuration: the sign-up form's fields, or communication preferences. */
  buyerPresets?: BuyerPresetOption[];
  guestPresets: GuestPresetOption[];
  showGuestTab?: boolean;
  titleOverride?: string;
  initialSelectedIds?: string[];
  draftQuestions?: DraftQuestionOption[];
  // Set 0 while swapping to CreateQuestionModal so the shared backdrop doesn't flicker.
  transitionDuration?: number;
}

export const AddQuestionModal: FC<AddQuestionModalProps> = ({
  isOpen,
  initialScope,
  formCategory,
  onClose,
  existingCustomProperties,
  isLoadingProperties,
  onAddSelected,
  onCreateFromScratch,
  onSearch,
  showFullAddressOption,
  showGuestFullAddressOption = false,
  showPhoneNumberOption,
  showNotificationPhoneOption = false,
  notificationPhoneLabel,
  showGuestPhoneNumberOption = false,
  buyerPresets = EMPTY_BUYER_PRESETS,
  guestPresets,
  showGuestTab = true,
  titleOverride,
  initialSelectedIds = EMPTY_SELECTED_IDS,
  draftQuestions = EMPTY_DRAFTS,
  transitionDuration,
}) => {
  const { t } = useTranslate();
  const wording = getStepQuestionsV4Wording(t, formCategory);
  const [scope, setScope] = useState<QuestionScope>(initialScope);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(initialSelectedIds));
  const [showNoSelectionError, setShowNoSelectionError] = useState(false);
  const lastLoggedSearchRef = useRef("");

  useEffect(() => {
    if (isOpen) {
      setScope(initialScope);
      setSearchQuery("");
      setSelectedIds(new Set(initialSelectedIds));
      setShowNoSelectionError(false);
      lastLoggedSearchRef.current = "";
    }
  }, [isOpen, initialScope, initialSelectedIds]);

  const availableProperties = useMemo(() => {
    const selectable = existingCustomProperties.filter(
      (property) => property.scope === scope && !property.scopesOnForm.includes(scope)
    );
    return [
      ...selectable.filter((property) => property.type === QuestionType.PhoneNumber),
      ...selectable.filter((property) => property.type !== QuestionType.PhoneNumber),
    ];
  }, [existingCustomProperties, scope]);

  const trimmedQuery = searchQuery.trim();

  const items: SelectableItem[] = useMemo(() => {
    const matchesQuery = (text: string) => matchesFuzzyQuery(text, trimmedQuery);

    const result: SelectableItem[] = [];

    for (const draft of draftQuestions) {
      if (draft.scope === scope && matchesQuery(draft.label)) {
        result.push({
          id: draft.id,
          label: draft.label,
          subtitle: draft.subtitle,
          "data-test": "v4-draft-question-row",
        });
      }
    }

    if (scope === "buyer") {
      if (showFullAddressOption && matchesQuery(t("dashboard", "stepQuestionsV4.dialog.fullAddress"))) {
        result.push({
          id: ADDRESS_OPTION_ID,
          label: t("dashboard", "stepQuestionsV4.dialog.fullAddress"),
          subtitle: t("dashboard", "stepQuestionsV4.dialog.typeAddress"),
          "data-test": "v4-address-option-row",
        });
      }
      if (showPhoneNumberOption && matchesQuery(t("dashboard", "stepQuestionsV4.dialog.phoneNumber"))) {
        result.push({
          id: PHONE_NUMBER_OPTION_ID,
          label: t("dashboard", "stepQuestionsV4.dialog.phoneNumber"),
          subtitle: t("dashboard", "donationForm.questionPhoneNumber"),
          "data-test": "v4-phone-number-option-row",
        });
      }
      if (showNotificationPhoneOption && notificationPhoneLabel !== undefined && matchesQuery(notificationPhoneLabel)) {
        result.push({
          id: NOTIFICATION_PHONE_OPTION_ID,
          label: notificationPhoneLabel,
          subtitle: t("dashboard", "donationForm.questionPhoneNumber"),
          "data-test": "v4-notification-phone-option-row",
        });
      }
      for (const preset of buyerPresets) {
        if (matchesQuery(preset.label)) {
          result.push({
            id: preset.id,
            label: preset.label,
            subtitle: preset.subtitle,
            "data-test": `v4-buyer-preset-${preset.id}`,
          });
        }
      }
    } else {
      if (showGuestFullAddressOption && matchesQuery(t("dashboard", "stepQuestionsV4.dialog.fullAddress"))) {
        result.push({
          id: GUEST_ADDRESS_OPTION_ID,
          label: t("dashboard", "stepQuestionsV4.dialog.fullAddress"),
          subtitle: t("dashboard", "stepQuestionsV4.dialog.typeAddress"),
          "data-test": "v4-guest-address-option-row",
        });
      }
      for (const preset of guestPresets) {
        if (matchesQuery(preset.label)) {
          result.push({
            id: preset.id,
            label: preset.label,
            subtitle: getQuestionTypeSubtitle(t, preset.questionType),
            "data-test": `v4-guest-preset-${preset.id}`,
          });
        }
      }
      if (showGuestPhoneNumberOption && matchesQuery(t("dashboard", "stepQuestionsV4.dialog.phoneNumber"))) {
        result.push({
          id: GUEST_PHONE_NUMBER_OPTION_ID,
          label: t("dashboard", "stepQuestionsV4.dialog.phoneNumber"),
          subtitle: t("dashboard", "donationForm.questionPhoneNumber"),
          "data-test": "v4-guest-phone-number-option-row",
        });
      }
    }

    const propertyPrefix = scope === "buyer" ? BUYER_PROPERTY_PREFIX : GUEST_PROPERTY_PREFIX;
    for (const property of availableProperties) {
      if (matchesQuery(property.body)) {
        result.push({
          id: `${propertyPrefix}${property.id}`,
          label: property.body,
          subtitle: getQuestionTypeSubtitle(t, property.type, property.choices),
          "data-test": `v4-property-row-${property.id}`,
        });
      }
    }

    return result;
  }, [
    trimmedQuery,
    scope,
    draftQuestions,
    showFullAddressOption,
    showPhoneNumberOption,
    showNotificationPhoneOption,
    notificationPhoneLabel,
    showGuestFullAddressOption,
    showGuestPhoneNumberOption,
    buyerPresets,
    guestPresets,
    availableProperties,
    t,
  ]);

  const handleToggle = (id: string) => {
    setShowNoSelectionError(false);
    setSelectedIds((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const buyerPresetIdSet = useMemo(() => new Set<string>(buyerPresets.map((preset) => preset.id)), [buyerPresets]);
  const guestPresetIdSet = useMemo(() => new Set<string>(guestPresets.map((preset) => preset.id)), [guestPresets]);
  const draftIdSet = useMemo(() => new Set<string>(draftQuestions.map((draft) => draft.id)), [draftQuestions]);

  // A preset can stop being offered while the user is away in the create modal, and its id must not linger
  // in the restored selection: it would count towards the CTA and then match no branch below.
  const selectableIds = useMemo(
    () =>
      [...selectedIds].filter(
        (id) =>
          draftIdSet.has(id) ||
          buyerPresetIdSet.has(id) ||
          guestPresetIdSet.has(id) ||
          id.startsWith(BUYER_PROPERTY_PREFIX) ||
          id.startsWith(GUEST_PROPERTY_PREFIX) ||
          (id === ADDRESS_OPTION_ID && showFullAddressOption) ||
          (id === GUEST_ADDRESS_OPTION_ID && showGuestFullAddressOption) ||
          id === PHONE_NUMBER_OPTION_ID ||
          id === GUEST_PHONE_NUMBER_OPTION_ID ||
          (id === NOTIFICATION_PHONE_OPTION_ID && showNotificationPhoneOption)
      ),
    [
      selectedIds,
      draftIdSet,
      buyerPresetIdSet,
      guestPresetIdSet,
      showFullAddressOption,
      showGuestFullAddressOption,
      showNotificationPhoneOption,
    ]
  );

  const handleAddSelected = () => {
    if (selectableIds.length === 0) {
      setShowNoSelectionError(true);
      return;
    }

    const selection: AddQuestionSelection = {
      addFullAddress: selectableIds.includes(ADDRESS_OPTION_ID),
      addGuestFullAddress: selectableIds.includes(GUEST_ADDRESS_OPTION_ID),
      addPhoneNumber: selectableIds.includes(PHONE_NUMBER_OPTION_ID),
      addGuestPhoneNumber: selectableIds.includes(GUEST_PHONE_NUMBER_OPTION_ID),
      addNotificationPhoneNumber: selectableIds.includes(NOTIFICATION_PHONE_OPTION_ID),
      buyerPresetIds: [],
      guestPresetIds: [],
      buyerPropertyIds: [],
      guestPropertyIds: [],
      draftIds: [],
    };

    for (const id of selectableIds) {
      if (draftIdSet.has(id)) {
        selection.draftIds.push(id);
      } else if (buyerPresetIdSet.has(id)) {
        selection.buyerPresetIds.push(id);
      } else if (guestPresetIdSet.has(id)) {
        selection.guestPresetIds.push(id);
      } else if (id.startsWith(BUYER_PROPERTY_PREFIX)) {
        selection.buyerPropertyIds.push(id.slice(BUYER_PROPERTY_PREFIX.length));
      } else if (id.startsWith(GUEST_PROPERTY_PREFIX)) {
        selection.guestPropertyIds.push(id.slice(GUEST_PROPERTY_PREFIX.length));
      }
    }

    onAddSelected(selection);
  };

  const specialOptionsCount =
    scope === "buyer"
      ? buyerPresets.length +
        Number(showFullAddressOption) +
        Number(showPhoneNumberOption) +
        Number(showNotificationPhoneOption)
      : guestPresets.length + Number(showGuestFullAddressOption) + Number(showGuestPhoneNumberOption);
  const draftsInScopeCount = draftQuestions.filter((draft) => draft.scope === scope).length;
  const showSearch = specialOptionsCount + draftsInScopeCount + availableProperties.length > 5;
  const showNoResults = trimmedQuery.length > 0 && items.length === 0;

  useEffect(() => {
    if (!onSearch || trimmedQuery.length === 0 || trimmedQuery === lastLoggedSearchRef.current) {
      return;
    }
    const timeout = setTimeout(() => {
      lastLoggedSearchRef.current = trimmedQuery;
      onSearch(scope, items.length > 0);
    }, 800);
    return () => clearTimeout(timeout);
  }, [trimmedQuery, items.length, scope, onSearch]);

  const modalTitle = (
    <DashboardIconBlock vibe="brand" size="large">
      <Add />
    </DashboardIconBlock>
  );

  return (
    <DashboardAdaptiveModal
      width="large"
      isOpen={isOpen}
      onClose={onClose}
      title={modalTitle}
      footerDivider
      transitionDuration={transitionDuration}
      actions={{
        main: {
          onClick: handleAddSelected,
          children:
            selectableIds.length > 0
              ? t("dashboard", "stepQuestionsV4.dialog.addQuestionCount", { count: selectableIds.length })
              : t("dashboard", "stepQuestionsV4.dialog.addQuestion"),
          "data-test": "v4-add-selected-button",
        },
        cancel: {
          onClick: onClose,
          children: t("dashboard", "donationForm.cancel"),
        },
      }}
    >
      <Stack spacing={2.5} width="100%">
        <DashboardTypography variant="h6">
          {titleOverride ?? (scope === "buyer" ? wording.buyerModalTitle : wording.attendeeModalTitle)}
        </DashboardTypography>

        {showGuestTab && <ScopeTogglePills value={scope} onChange={setScope} formCategory={formCategory} />}

        {showSearch && (
          <DashboardSearchField
            placeholder={t("dashboard", "stepQuestionsV4.dialog.searchPlaceholder")}
            value={searchQuery}
            onChange={setSearchQuery}
          />
        )}

        {isLoadingProperties ? (
          <Stack spacing={1}>
            <DashboardSkeleton variant="rounded" height={56} />
            <DashboardSkeleton variant="rounded" height={56} />
            <DashboardSkeleton variant="rounded" height={56} />
          </Stack>
        ) : showNoResults ? (
          <Stack spacing={1.5} alignItems="flex-start" data-test="v4-no-results" sx={{ pb: 2 }}>
            <DashboardTypography variant="body2" vibe="text-neutral-quiet">
              {t("dashboard", "stepQuestionsV4.dialog.noResults")}
            </DashboardTypography>
            <ButtonBase
              onClick={() => onCreateFromScratch(scope, [...selectedIds], trimmedQuery, "Search No Results")}
              data-test="v4-create-from-search-button"
              sx={createActionSx}
            >
              <DashboardIconBlock vibe="brand" size="medium" sx={{ flexShrink: 0 }}>
                <Edit />
              </DashboardIconBlock>
              <DashboardTypography variant="subtitle2" vibe="text-brand-moderate">
                {t("dashboard", "stepQuestionsV4.dialog.createFromSearch")}
              </DashboardTypography>
              <DashboardTypography variant="body2" vibe="text-brand-moderate">
                {trimmedQuery}
              </DashboardTypography>
            </ButtonBase>
          </Stack>
        ) : (
          <Stack spacing={1} sx={scrollableListSx}>
            <ButtonBase
              onClick={() => onCreateFromScratch(scope, [...selectedIds], undefined, "Modal")}
              data-test="v4-create-from-scratch-button"
              sx={createActionSx}
            >
              <DashboardIconBlock vibe="brand" size="medium" sx={{ flexShrink: 0 }}>
                <Edit />
              </DashboardIconBlock>
              <DashboardTypography variant="subtitle2" vibe="text-brand-moderate">
                {t("dashboard", "stepQuestionsV4.dialog.createFromScratch")}
              </DashboardTypography>
            </ButtonBase>

            {items.length > 0 && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Divider sx={{ flex: 1 }} />
                <DashboardTypography variant="body2" vibe="text-neutral-supershy" sx={{ textTransform: "uppercase" }}>
                  {t("dashboard", "common.or")}
                </DashboardTypography>
                <Divider sx={{ flex: 1 }} />
              </Box>
            )}

            <ExistingQuestionsList items={items} selectedIds={selectedIds} onToggle={handleToggle} />
          </Stack>
        )}

        {showNoSelectionError && (
          <DashboardTypography variant="body2" vibe="text-danger-moderate" data-test="v4-no-selection-error">
            {t("dashboard", "stepQuestionsV4.dialog.noSelectionError")}
          </DashboardTypography>
        )}
      </Stack>
    </DashboardAdaptiveModal>
  );
};
